// 17.09.25 12:25 - Custom password reset page matching COOC signup design
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import coocLogo from '../../assets/cooc-logo-black.png';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../firebase';
import './Auth.css';

const CustomPasswordReset = () => {
  const [searchParams] = useSearchParams();
  const [oobCode, setOobCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeValid, setCodeValid] = useState(false);

  useEffect(() => {
    // Get the code from URL parameters
    const code = searchParams.get('oobCode');
    if (code) {
      setOobCode(code);
      verifyResetCode(code);
    } else {
      setError('Invalid or missing password reset code.');
    }
  }, [searchParams]);

  const verifyResetCode = async (code) => {
    try {
      const userEmail = await verifyPasswordResetCode(auth, code);
      setEmail(userEmail);
      setCodeValid(true);
    } catch (error) {
      setError('Invalid or expired password reset link.');
      setCodeValid(false);
    }
  };

  const validatePassword = () => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      match: password && confirmPassword && password === confirmPassword
    };
    
    return Object.values(requirements).every(req => req === true);
  };

  const getRequirementStatus = (requirement) => {
    switch (requirement) {
      case 'length':
        return password.length >= 8;
      case 'uppercase':
        return /[A-Z]/.test(password);
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'number':
        return /\d/.test(password);
      case 'special':
        return /[!@#$%^&*(),.?":{}|<>]/.test(password);
      case 'match':
        return password && confirmPassword && password === confirmPassword;
      default:
        return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validatePassword()) {
      setError('Please meet all password requirements.');
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message);
    }
    setLoading(false);
  };

  if (!codeValid && !error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
          <div 
  className="cooc-logo" 
  style={{ backgroundImage: `url(${coocLogo})` }}
></div>
            <h1>Verifying Reset Link</h1>
            <p>Please wait while we verify your password reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !codeValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
          <div 
  className="cooc-logo" 
  style={{ backgroundImage: `url(${coocLogo})` }}
></div>
            <h1>Invalid Reset Link</h1>
            <p>This password reset link is invalid or has expired.</p>
          </div>
          
          <div className="error-message">{error}</div>
          
          <div className="form-actions">
            <Link to="/forgot-password" className="auth-button">
              Request New Reset Link
            </Link>
            <a href="/cooc-login.html" className="secondary-button">
              Back to Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        {!success ? (
          <>
            <div className="auth-header">
            <div 
  className="cooc-logo" 
  style={{ backgroundImage: `url(${coocLogo})` }}
></div>
              <h1>Set New Password</h1>
              <p>Create a secure password for your COOC account</p>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '100%'}}></div>
              </div>
            </div>

            <div className="email-display">
              <div className="email-address">{email}</div>
              <div className="email-note">Your COOC account email</div>
            </div>

            <form onSubmit={handleSubmit} className="password-form">
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a secure password"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="form-input"
                />
              </div>

              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <ul>
                  <li className={getRequirementStatus('length') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('length') ? '✓' : '○'}</span>
                    At least 8 characters long
                  </li>
                  <li className={getRequirementStatus('uppercase') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('uppercase') ? '✓' : '○'}</span>
                    Contains at least one uppercase letter
                  </li>
                  <li className={getRequirementStatus('lowercase') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('lowercase') ? '✓' : '○'}</span>
                    Contains at least one lowercase letter
                  </li>
                  <li className={getRequirementStatus('number') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('number') ? '✓' : '○'}</span>
                    Contains at least one number
                  </li>
                  <li className={getRequirementStatus('special') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('special') ? '✓' : '○'}</span>
                    Contains a special character (!@#$%^&*)
                  </li>
                  <li className={getRequirementStatus('match') ? 'valid' : 'invalid'}>
                    <span className="icon">{getRequirementStatus('match') ? '✓' : '○'}</span>
                    Both passwords match
                  </li>
                </ul>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                disabled={loading || !validatePassword()} 
                className="auth-button"
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </button>
            </form>
          </>
        ) : (
          <div className="success-step">
            <div className="success-icon">🎉</div>
            <h3>Password Updated Successfully!</h3>
            <div className="success-info">
              <p>Your COOC account password has been updated.</p>
              <p>You can now sign in with your new password.</p>
            </div>

            <div className="next-steps">
              <h4>Next Steps:</h4>
              <ol>
                <li>Go to the COOC sign-in page</li>
                <li>Enter your email: <strong>{email}</strong></li>
                <li>Use your new password</li>
                <li>Access your document management platform</li>
              </ol>
            </div>

            <div className="form-actions">
            <a href="/cooc-login.html" className="auth-button">
  Sign In Now
</a>
            </div>
          </div>
        )}

        <div className="auth-footer">
          <p>
            Need help? Contact <strong>accounts@cooc.com.au</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomPasswordReset;